import React, { useState, useEffect, useContext } from "react";
import { Plus, Trash2, Save, RefreshCw, RefreshCcw, ChevronDown, ChevronRight, X } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";
import CreatableSelect from "react-select/creatable";
import api from "../api";
import {
  getCostingSettings,
  updatePrivateLabelCustomerMappings,
  syncErpagCustomersToMappings,
  getErpagPlCategories
} from "../api/costingSettingsApi";
import { getToken } from "../Auth/Token";

const DEFAULT_MAPPINGS = [];

export default function PrivateLabelCustomerMappingSection() {
  const { canWrite, user } = useContext(AuthContext);
  const canManage = canWrite("AccessCosting", "CostingSettings");

  const [mappings, setMappings] = useState([]);
  const [originalMappings, setOriginalMappings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Expanded state for Accordion
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // Dropdown options
  const [customerOptions, setCustomerOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    loadData();
    loadDropdownOptions();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getCostingSettings();
      const loaded = res.data?.privateLabelCustomerMappings;
      if (Array.isArray(loaded) && loaded.length > 0) {
        setMappings(loaded.map(m => ({ ...m, _uid: m._uid || Math.random().toString(36).substring(2, 9) })));
      } else {
        setMappings(DEFAULT_MAPPINGS.map(m => ({ ...m, _uid: Math.random().toString(36).substring(2, 9) })));
      }
    } catch (err) {
      console.error("Failed to load PL Customer Mappings:", err);
      toast.error("Failed to load Private Label Customer Mappings");
      setMappings(DEFAULT_MAPPINGS);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownOptions = async () => {
    try {
      // Load Customers
      const token = getToken();
      const custRes = await api.get("/api/customers", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (custRes.data?.success) {
        const cOptions = custRes.data.data.map(c => ({
          label: c.customerName || c.key,
          value: c.key, // erpagCustomerId
          customerName: c.customerName
        }));
        setCustomerOptions(cOptions);
      }

      // Load Categories
      const catRes = await getErpagPlCategories();
      if (catRes?.success) {
        const cats = catRes.data || [];
        setCategoryOptions(cats.map(c => ({ label: c, value: c })));
      }
    } catch (err) {
      console.error("Failed to load dropdown options:", err);
    }
  };

  const handleSyncAllErpagCustomers = async () => {
    setSyncing(true);
    try {
      const res = await syncErpagCustomersToMappings();
      if (res?.success) {
        toast.success(res.message || "Synced all ERPAG customers into DB!");
        const updated = res.data?.privateLabelCustomerMappings;
        if (Array.isArray(updated)) {
          setMappings(updated.map(m => ({ ...m, _uid: m._uid || Math.random().toString(36).substring(2, 9) })));
        }
      } else {
        toast.error("Failed to sync ERPAG customers");
      }
    } catch (err) {
      console.error("Sync ERPAG Customers Error:", err);
      toast.error("Failed to sync customers from ERPAG");
    } finally {
      setSyncing(false);
    }
  };

  const handleStartEdit = () => {
    setOriginalMappings(JSON.parse(JSON.stringify(mappings)));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setMappings(originalMappings);
    setIsEditing(false);
  };

  const toggleCustomerExpand = (uid) => {
    setExpandedCustomers(prev => ({ ...prev, [uid]: !prev[uid] }));
  };

  // ─── Customer Level Functions ──────────────────────────────────────────

  const handleAddCustomer = () => {
    const newUid = Math.random().toString(36).substring(2, 9);
    setMappings((prev) => [
      {
        _uid: newUid,
        customerName: "",
        erpagCustomerId: "",
        categories: [],
        notes: "",
        updatedBy: user?.email || user?.firstName || "Draft",
        updatedAt: new Date(),
      },
      ...prev,
    ]);
    setExpandedCustomers(prev => ({ ...prev, [newUid]: true }));
  };

  const handleRemoveCustomer = (idx) => {
    setMappings((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCustomerChange = async (idx, selectedOption) => {
    if (!selectedOption) return;
    
    let newCustomerName = selectedOption.label;
    let newErpagCustomerId = selectedOption.value;

    // Handle Create New Customer
    if (selectedOption.__isNew__) {
      try {
        const token = getToken();
        const res = await api.post("/api/customers", { customerName: newCustomerName }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.success) {
          if (res.data.data.localOnly) {
            toast.error("Created locally! API endpoint missing in ERPAG. Please add manually.");
          } else {
            toast.success("Created new customer in ERPAG!");
          }
          newErpagCustomerId = res.data.data.key;
          // Add to options
          setCustomerOptions(prev => [...prev, { label: newCustomerName, value: newErpagCustomerId }]);
        }
      } catch (err) {
        toast.error("Failed to create customer in ERPAG");
        console.error(err);
        return;
      }
    }

    setMappings((prev) =>
      prev.map((m, i) =>
        i === idx ? { ...m, customerName: newCustomerName, erpagCustomerId: newErpagCustomerId } : m
      )
    );
  };

  // ─── Category Level Functions ──────────────────────────────────────────

  const handleAddCategory = (custIdx) => {
    setMappings(prev => {
      const next = [...prev];
      if (!next[custIdx].categories) next[custIdx].categories = [];
      next[custIdx].categories.push({
        categoryName: "",
        erpagCategoryId: "",
        prefixes: []
      });
      return next;
    });
  };

  const handleRemoveCategory = (custIdx, catIdx) => {
    setMappings(prev => {
      const next = [...prev];
      next[custIdx].categories = next[custIdx].categories.filter((_, i) => i !== catIdx);
      return next;
    });
  };

  const handleCategoryChange = (custIdx, catIdx, selectedOption) => {
    if (!selectedOption) return;
    setMappings(prev => {
      const next = [...prev];
      next[custIdx].categories[catIdx].categoryName = selectedOption.label;
      next[custIdx].categories[catIdx].erpagCategoryId = selectedOption.__isNew__ ? "" : selectedOption.value;
      return next;
    });
  };

  // ─── Prefix Level Functions ───────────────────────────────────────────

  const handleAddPrefix = (custIdx, catIdx) => {
    setMappings(prev => {
      const next = [...prev];
      if (!next[custIdx].categories[catIdx].prefixes) next[custIdx].categories[catIdx].prefixes = [];
      next[custIdx].categories[catIdx].prefixes.push({
        formatSuffix: "",
        skuPrefix: "",
        packagingPrefix: ""
      });
      return next;
    });
  };

  const handleRemovePrefix = (custIdx, catIdx, prefIdx) => {
    setMappings(prev => {
      const next = [...prev];
      next[custIdx].categories[catIdx].prefixes = next[custIdx].categories[catIdx].prefixes.filter((_, i) => i !== prefIdx);
      return next;
    });
  };

  const handlePrefixFieldChange = (custIdx, catIdx, prefIdx, field, value) => {
    setMappings(prev => {
      const next = [...prev];
      next[custIdx].categories[catIdx].prefixes[prefIdx][field] = value;
      return next;
    });
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      const currentUserEmail = user?.email || (user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "Admin");
      const cleaned = mappings.map((m) => {
        const { _uid, ...rest } = m;
        return {
          ...rest,
          customerName: String(m.customerName || "").trim(),
          erpagCustomerId: String(m.erpagCustomerId || "").trim(),
          updatedBy: currentUserEmail,
        };
      }).filter(m => m.customerName);

      const res = await updatePrivateLabelCustomerMappings(cleaned);
      if (res?.success) {
        toast.success("Private Label Customer Mappings updated successfully");
        const freshData = res.data.privateLabelCustomerMappings || cleaned;
        setMappings(freshData.map(m => ({ ...m, _uid: Math.random().toString(36).substring(2, 9) })));
        setIsEditing(false);
      } else {
        toast.error("Failed to update Private Label Customer Mappings");
      }
    } catch (err) {
      console.error("Save PL Customer Mappings Error:", err);
      toast.error("Failed to save Private Label Customer Mappings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center text-gray-500 gap-2">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading PL Customer Mappings...
      </div>
    );
  }

  // React-select custom styles for embedded rows
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      fontSize: '14px',
      borderRadius: '0.375rem',
      borderColor: '#D1D5DB',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#9CA3AF'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
    }),
    input: (base) => ({
      ...base,
      margin: '0',
      padding: '0',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '30px',
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">Private Label Customer & Category Prefix Mapping</h2>
          <p className="text-sm text-gray-500 mt-1">
            Map Client + ERPAG Category + Region/Format Suffix to their exact Finished Goods Prefix & Packaging Prefix (e.g. Youngevity CA Pouch = 7810, US Pouch = 7813).
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-nowrap">
          <button
            type="button"
            onClick={handleSyncAllErpagCustomers}
            disabled={syncing || !canManage}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors shadow-sm disabled:opacity-50"
          >
            <RefreshCcw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing ERPAG Customers..." : "Sync All Customers from ERPAG"}
          </button>

          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !canManage}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Mappings"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleStartEdit}
              disabled={!canManage}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-black text-white rounded-xl hover:bg-gray-800 transition-colors shadow-sm disabled:opacity-50"
            >
              <FaEdit /> Edit Mappings
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">
          Customer & Category Mappings ({mappings.length} Customers)
        </h3>
        {isEditing && (
          <button
            type="button"
            onClick={handleAddCustomer}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Customer
          </button>
        )}
      </div>

      <div className="space-y-4">
        {mappings.length === 0 ? (
          <div className="py-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
            No Private Label customer mappings configured. Click "Sync All Customers from ERPAG" or "Add Customer".
          </div>
        ) : (
          mappings.map((customer, custIdx) => {
            const uid = customer._uid || custIdx;
            const isExpanded = expandedCustomers[uid];
            return (
              <div key={`cust-${uid}`} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                
                {/* Master Row (Customer) */}
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50 border-b border-gray-200' : 'hover:bg-gray-50'}`}
                  onClick={() => toggleCustomerExpand(uid)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                    
                    {isEditing ? (
                      <div className="flex-1 max-w-sm" onClick={(e) => e.stopPropagation()}>
                        <CreatableSelect
                          options={customerOptions}
                          styles={selectStyles}
                          value={customer.customerName ? { label: customer.customerName, value: customer.erpagCustomerId } : null}
                          onChange={(val) => handleCustomerChange(custIdx, val)}
                          placeholder="Select or Create Customer..."
                          formatCreateLabel={(inputValue) => `Create new customer "${inputValue}" in ERPAG`}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    ) : (
                      <div className="font-bold text-gray-900 text-lg">{customer.customerName || "Unnamed Customer"}</div>
                    )}

                    {!isEditing && customer.erpagCustomerId && (
                       <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded text-xs ml-2">
                         ID: {customer.erpagCustomerId}
                       </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-500">
                      {customer.categories?.length || 0} Categories
                    </div>
                    {isEditing && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveCustomer(custIdx); }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove Customer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded Details (Categories) */}
                {isExpanded && (
                  <div className="p-5 bg-white space-y-6">
                    {(customer.categories || []).length === 0 ? (
                      <div className="text-sm text-gray-400 italic py-2">No categories assigned to this customer.</div>
                    ) : (
                      (customer.categories || []).map((cat, catIdx) => (
                        <div key={`cat-${custIdx}-${catIdx}`} className="bg-gray-50 rounded-lg border border-gray-200 p-4 relative">
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleRemoveCategory(custIdx, catIdx)}
                              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          
                          <div className="mb-4 pr-8">
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Category</label>
                            {isEditing ? (
                              <div className="max-w-md">
                                <CreatableSelect
                                  options={categoryOptions}
                                  styles={selectStyles}
                                  value={cat.categoryName ? { label: cat.categoryName, value: cat.categoryName } : null}
                                  onChange={(val) => handleCategoryChange(custIdx, catIdx, val)}
                                  placeholder="Select or Create Category..."
                                  formatCreateLabel={(inputValue) => `Use new category "${inputValue}"`}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                />
                              </div>
                            ) : (
                              <div className="font-semibold text-gray-800 text-sm">
                                {cat.categoryName || "Unnamed Category"}
                              </div>
                            )}
                          </div>

                          {/* Prefixes Table */}
                          <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                            <table className="w-full text-sm text-left">
                              <thead className="bg-gray-100/50 text-xs font-semibold text-gray-600 uppercase">
                                <tr>
                                  <th className="px-4 py-2 w-1/3">Format / Region Suffix</th>
                                  <th className="px-4 py-2 w-1/4">FG SKU Prefix</th>
                                  <th className="px-4 py-2 w-1/4">Pkg SKU Prefix</th>
                                  {isEditing && <th className="px-4 py-2 text-center w-16">Action</th>}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(cat.prefixes || []).length === 0 ? (
                                  <tr>
                                    <td colSpan={isEditing ? 4 : 3} className="px-4 py-3 text-center text-gray-400 text-xs italic">
                                      No format prefixes defined.
                                    </td>
                                  </tr>
                                ) : (
                                  (cat.prefixes || []).map((pref, prefIdx) => (
                                    <tr key={`pref-${custIdx}-${catIdx}-${prefIdx}`}>
                                      <td className="px-4 py-2">
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={pref.formatSuffix || ""}
                                            onChange={(e) => handlePrefixFieldChange(custIdx, catIdx, prefIdx, "formatSuffix", e.target.value)}
                                            placeholder="e.g. CA Pouch"
                                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-black outline-none text-sm font-medium"
                                          />
                                        ) : (
                                          <span className="font-medium text-gray-700">{pref.formatSuffix || "—"}</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2">
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={pref.skuPrefix || ""}
                                            onChange={(e) => handlePrefixFieldChange(custIdx, catIdx, prefIdx, "skuPrefix", e.target.value)}
                                            placeholder="e.g. 7810"
                                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-black outline-none text-sm font-mono"
                                          />
                                        ) : (
                                          <span className="font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs">{pref.skuPrefix || "—"}</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2">
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={pref.packagingPrefix || ""}
                                            onChange={(e) => handlePrefixFieldChange(custIdx, catIdx, prefIdx, "packagingPrefix", e.target.value)}
                                            placeholder="e.g. 9810"
                                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-black outline-none text-sm font-mono"
                                          />
                                        ) : (
                                          <span className="font-mono bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded text-xs">{pref.packagingPrefix || "—"}</span>
                                        )}
                                      </td>
                                      {isEditing && (
                                        <td className="px-4 py-2 text-center">
                                          <button
                                            type="button"
                                            onClick={() => handleRemovePrefix(custIdx, catIdx, prefIdx)}
                                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                            {isEditing && (
                              <div className="bg-gray-50/50 px-4 py-2 border-t border-gray-100">
                                <button
                                  type="button"
                                  onClick={() => handleAddPrefix(custIdx, catIdx)}
                                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" /> Add Format/Prefix
                                </button>
                              </div>
                            )}
                          </div>

                        </div>
                      ))
                    )}

                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => handleAddCategory(custIdx)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-dashed border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors w-full justify-center"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Another Category
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
